/**
 * Reservation capacity helpers: free tables for a slot, total capacity, tables that fit party size,
 * and event-scoped slot guest aggregation for buffet / multi-day events.
 */

import type { Table, Reservation, Event, ReservationStatus } from '../types';

/** Reservations that count toward per-slot guest limits (not waitlist). */
export const SLOT_GUEST_COUNT_STATUSES: ReservationStatus[] = ['pending', 'confirmed', 'seated'];

export function isBuffetMultiSlotEvent(event: Event): boolean {
  if (event.bookingFormMode === 'standard') return false;
  if (event.bookingFormMode === 'buffet_multi_slot') return true;
  if ((event.mealSlots?.length ?? 0) > 0) return true;
  if ((event.eventDates?.length ?? 0) > 1) return true;
  return false;
}

export function getEventBookingDates(event: Event): string[] {
  const extra = event.eventDates?.filter(Boolean) ?? [];
  if (extra.length > 0) return [...new Set(extra)].sort();
  return [event.eventDate];
}

/**
 * Next event to pre-select on the reservations page: earliest by start date among
 * events still running (last day ≥ today) or, if none, earliest event that starts on or after today.
 */
export function getNextEventIdForDefaultFilter(events: Event[], todayIso: string): 'all' | string {
  if (!events.length) return 'all';
  type Row = { id: string; first: string; last: string };
  const rows: Row[] = events
    .map((ev) => {
      const sorted = [...getEventBookingDates(ev)].filter(Boolean).sort();
      if (!sorted.length) return null;
      return { id: String(ev.id), first: sorted[0], last: sorted[sorted.length - 1] };
    })
    .filter((r): r is Row => r != null);

  const ongoingOrUpcoming = rows
    .filter((r) => r.last >= todayIso)
    .sort((a, b) => a.first.localeCompare(b.first) || a.id.localeCompare(b.id));
  if (ongoingOrUpcoming.length) return ongoingOrUpcoming[0].id;

  const futureStart = rows
    .filter((r) => r.first >= todayIso)
    .sort((a, b) => a.first.localeCompare(b.first) || a.id.localeCompare(b.id));
  if (futureStart.length) return futureStart[0].id;

  return 'all';
}

/** First event date on or after `todayIso`, else the event’s earliest date. */
export function firstEventDateOnOrAfter(event: Event, todayIso: string): string | undefined {
  const sorted = [...getEventBookingDates(event)].filter(Boolean).sort();
  if (!sorted.length) return undefined;
  return sorted.find((d) => d >= todayIso) ?? sorted[0];
}

export function eventAppliesToReservationDate(event: Event, reservationDate: string): boolean {
  return getEventBookingDates(event).includes(reservationDate);
}

export function getReservationSlotLabel(r: Reservation, event?: Event | null): string | undefined {
  if (!event?.mealSlots?.length || !r.slotId) return undefined;
  return event.mealSlots.find((s) => s.id === r.slotId)?.label;
}

function matchesEventSlotSlice(
  r: Reservation,
  eventId: string | number,
  reservationDate: string,
  reservationTime: string,
  slotId?: string
): boolean {
  if (String(r.eventId ?? '') !== String(eventId)) return false;
  if (r.reservationDate !== reservationDate) return false;
  if (r.reservationTime !== reservationTime) return false;
  if (slotId) {
    if (r.slotId && r.slotId !== slotId) return false;
  }
  return true;
}

function reservationAdultsForAggregate(r: Reservation): number {
  if (typeof r.adultsCount === 'number') return r.adultsCount;
  const kb = r.kidsBelow10Count ?? 0;
  const ka = r.kidsAbove10Count ?? 0;
  if (kb > 0 || ka > 0) return Math.max(0, r.partySize - kb - ka);
  if (typeof r.kidsCount === 'number') return Math.max(0, r.partySize - r.kidsCount);
  return r.partySize;
}

function reservationKidsBelow10ForAggregate(r: Reservation): number {
  if (typeof r.kidsBelow10Count === 'number') return r.kidsBelow10Count;
  if (typeof r.kidsCount === 'number') return r.kidsCount;
  return 0;
}

function reservationKids10PlusForAggregate(r: Reservation): number {
  return typeof r.kidsAbove10Count === 'number' ? r.kidsAbove10Count : 0;
}

/** Total children (under-10 + 10+ bands, or legacy kidsCount). */
function reservationKidsTotalForAggregate(r: Reservation): number {
  const kb = reservationKidsBelow10ForAggregate(r);
  const ka = reservationKids10PlusForAggregate(r);
  if (kb > 0 || ka > 0) return kb + ka;
  if (typeof r.kidsCount === 'number') return r.kidsCount;
  return 0;
}

/** Sum party size and guest breakdown for a list (e.g. staff dashboard filtered rows). */
export function summarizeReservationGuestTotals(reservations: Reservation[]): {
  bookingCount: number;
  partySize: number;
  adults: number;
  kids: number;
} {
  let partySize = 0;
  let adults = 0;
  let kids = 0;
  for (const r of reservations) {
    partySize += r.partySize;
    adults += reservationAdultsForAggregate(r);
    kids += reservationKidsTotalForAggregate(r);
  }
  return {
    bookingCount: reservations.length,
    partySize,
    adults,
    kids,
  };
}

export interface EventSlotGuestTotals {
  partyBooked: number;
  adultsBooked: number;
  kidsBelow10Booked: number;
  kids10PlusBooked: number;
}

/**
 * Sum party and guest breakdown for one event + date + time (and optional slot id).
 * Only reservations in `statuses` are included (default: pending, confirmed, seated).
 */
export function aggregateEventSlotGuests(
  reservations: Reservation[],
  eventId: string | number,
  reservationDate: string,
  reservationTime: string,
  options?: {
    slotId?: string;
    excludeReservationId?: number;
    statuses?: ReservationStatus[];
  }
): EventSlotGuestTotals {
  const statuses = options?.statuses ?? SLOT_GUEST_COUNT_STATUSES;
  let partyBooked = 0;
  let adultsBooked = 0;
  let kidsBelow10Booked = 0;
  let kids10PlusBooked = 0;
  for (const r of reservations) {
    if (options?.excludeReservationId != null && r.id === options.excludeReservationId) continue;
    if (!statuses.includes(r.status)) continue;
    if (!matchesEventSlotSlice(r, eventId, reservationDate, reservationTime, options?.slotId))
      continue;
    partyBooked += r.partySize;
    adultsBooked += reservationAdultsForAggregate(r);
    kidsBelow10Booked += reservationKidsBelow10ForAggregate(r);
    kids10PlusBooked += reservationKids10PlusForAggregate(r);
  }
  return { partyBooked, adultsBooked, kidsBelow10Booked, kids10PlusBooked };
}

export function getEffectiveSlotGuestLimit(event: Event, slotId?: string): number | undefined {
  if (slotId && event.slotGuestLimits && event.slotGuestLimits[slotId] != null) {
    const n = Number(event.slotGuestLimits[slotId]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  if (event.maxGuestsPerSlot != null) {
    const n = Number(event.maxGuestsPerSlot);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

export function getEffectiveCapacitySource(event: Event): 'slot' | 'tables' | 'both' {
  if (event.capacitySource) return event.capacitySource;
  const lim = getEffectiveSlotGuestLimit(event, undefined);
  const perSlot =
    event.slotGuestLimits &&
    Object.values(event.slotGuestLimits).some((v) => v != null && Number(v) >= 0);
  return lim != null || perSlot ? 'both' : 'tables';
}

/** Remaining guest “seats” for the slot limit, or null if unlimited. */
export function getRemainingSlotPartyCapacity(
  event: Event,
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  slotId: string | undefined,
  excludeReservationId?: number
): number | null {
  const limit = getEffectiveSlotGuestLimit(event, slotId);
  if (limit == null) return null;
  const { partyBooked } = aggregateEventSlotGuests(
    reservations,
    event.id,
    reservationDate,
    reservationTime,
    { slotId, excludeReservationId }
  );
  return Math.max(0, limit - partyBooked);
}

export function buffetPartyFitsSlot(
  event: Event,
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  slotId: string | undefined,
  partySize: number,
  excludeReservationId?: number
): boolean {
  const rem = getRemainingSlotPartyCapacity(
    event,
    reservations,
    reservationDate,
    reservationTime,
    slotId,
    excludeReservationId
  );
  if (rem === null) return true;
  return rem >= partySize;
}

/**
 * Check if a table is taken for the given date/time by any reservation.
 * A table is taken if any reservation has the same date and time and either
 * tableId matches or assignedTableIds contains the table id.
 */
function isTableTakenForSlot(
  tableId: number,
  reservationDate: string,
  reservationTime: string,
  reservations: Reservation[],
  excludeReservationId?: number
): boolean {
  return reservations.some((r) => {
    if (r.id === excludeReservationId) return false;
    if (r.reservationDate !== reservationDate || r.reservationTime !== reservationTime)
      return false;
    if (r.tableId === tableId) return true;
    if (r.assignedTableIds?.includes(tableId)) return true;
    return false;
  });
}

/**
 * Tables that are free (not reserved) for the given date and time.
 */
export function getTablesFreeForSlot(
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  excludeReservationId?: number
): Table[] {
  return tables.filter(
    (t) =>
      !isTableTakenForSlot(
        t.id,
        reservationDate,
        reservationTime,
        reservations,
        excludeReservationId
      )
  );
}

/**
 * Total available seating capacity for the given date/time.
 * Optionally capped by event maxCapacity.
 */
export function getTotalAvailableCapacity(
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  eventMaxCapacity?: number,
  excludeReservationId?: number
): number {
  const free = getTablesFreeForSlot(
    tables,
    reservations,
    reservationDate,
    reservationTime,
    excludeReservationId
  );
  const sum = free.reduce((acc, t) => acc + t.capacity, 0);
  if (eventMaxCapacity != null && eventMaxCapacity >= 0) {
    return Math.min(sum, eventMaxCapacity);
  }
  return sum;
}

/**
 * Tables that are free for the slot and have capacity >= partySize.
 */
export function getTablesFittingParty(
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  partySize: number,
  excludeReservationId?: number
): Table[] {
  const free = getTablesFreeForSlot(
    tables,
    reservations,
    reservationDate,
    reservationTime,
    excludeReservationId
  );
  return free.filter((t) => t.capacity >= partySize);
}

/**
 * Max single-table capacity among all tables (for deciding if party fits one table).
 */
export function getMaxSingleTableCapacity(tables: Table[]): number {
  if (tables.length === 0) return 0;
  return Math.max(...tables.map((t) => t.capacity));
}

/**
 * Whether the selected table has enough capacity for the party.
 */
export function tableFitsParty(table: Table | null, partySize: number): boolean {
  if (!table) return false;
  return table.capacity >= partySize;
}

export interface BuffetCapacityCheck {
  mustWaitlist: boolean;
  requiresMerge: boolean;
  slotOk: boolean;
  tablesOk: boolean;
  remainingSlotGuests: number | null;
}

/**
 * Combined slot limit + table capacity for public buffet booking.
 * `capacitySource`: slot = only slot limit; tables = only venue tables; both = require both.
 */
export function evaluateBuffetBookingCapacity(
  event: Event,
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  slotId: string | undefined,
  partySize: number,
  excludeReservationId?: number
): BuffetCapacityCheck {
  const maxSingle = getMaxSingleTableCapacity(tables);
  const totalCap = getTotalAvailableCapacity(
    tables,
    reservations,
    reservationDate,
    reservationTime,
    event.maxCapacity,
    excludeReservationId
  );
  const src = getEffectiveCapacitySource(event);
  const rem = getRemainingSlotPartyCapacity(
    event,
    reservations,
    reservationDate,
    reservationTime,
    slotId,
    excludeReservationId
  );
  const slotOk = rem === null || rem >= partySize;
  const tablesOk = totalCap >= partySize;
  let mustWaitlist = false;
  if (src === 'slot') mustWaitlist = !slotOk;
  else if (src === 'tables') mustWaitlist = !tablesOk;
  else mustWaitlist = !slotOk || !tablesOk;
  const canSeatByTables = tablesOk && !mustWaitlist;
  const requiresMerge = partySize > maxSingle && canSeatByTables;
  return {
    mustWaitlist,
    requiresMerge,
    slotOk,
    tablesOk,
    remainingSlotGuests: rem,
  };
}
